import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  DevHubTextField,
  DevHubFormControl,
  DevHubAutocomplete,
} from '@devxdiscover/devhub-ui';
import {
  useApi,
  configApiRef,
  identityApiRef,
} from '@backstage/core-plugin-api';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder';
import { CustomFieldExtensionSchema } from '@backstage/plugin-scaffolder-react';

/**
 * The schema for the {@link ArtifactoryPermissionPicker} return value.
 */
export const ArtifactoryPermissionPickerSchema: CustomFieldExtensionSchema = {
  returnValue: {
    type: 'string',
    title: 'Permission Target Name',
    description: 'An existing Artifactory permission target name',
  },
};

/**
 * A scaffolder field extension that provides an autocomplete picker
 * for Artifactory permission targets. Fetches the full list from the
 * DevHub backend REST API and filters client-side.
 * @param props The field extension props provided by the scaffolder
 * @returns The selected permission target name (string) or undefined.
 */
export const ArtifactoryPermissionPicker = (
  props: FieldExtensionComponentProps<string | undefined>,
) => {
  const { formData, onChange, rawErrors, idSchema, required } = props;
  const [permissions, setPermissions] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const config = useApi(configApiRef);
  const identityApi = useApi(identityApiRef);
  const backendUrl = config.getString('backend.baseUrl');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    (async () => {
      try {
        const { token } = await identityApi.getCredentials();
        const response = await fetch(
          `${backendUrl}/api/det-portal/artifactory/permissions`,
          {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          },
        );
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const fetched: string[] = await response.json();
        if (!cancelled) setPermissions([...fetched].sort());
      } catch {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [backendUrl, identityApi]);

  const filteredOptions = useMemo(() => {
    if (!inputValue.trim()) return permissions;
    const q = inputValue.toLowerCase();
    return permissions.filter(p => p.toLowerCase().includes(q));
  }, [permissions, inputValue]);

  const handleSelect = useCallback(
    (_: unknown, value: string | null) => {
      onChange(value ?? undefined);
    },
    [onChange],
  );

  return (
    <DevHubFormControl
      margin="normal"
      required={required}
      error={rawErrors && rawErrors.length > 0 && !formData}
    >
      {error ? (
        <DevHubTextField
          error
          label="Error"
          defaultValue="Failed to load permissions"
          helperText="Could not fetch permission targets."
          variant="outlined"
        />
      ) : (
        <DevHubAutocomplete
          id={idSchema?.$id}
          value={formData ?? null}
          loading={loading}
          loadingText="Loading permissions..."
          options={filteredOptions}
          getOptionLabel={(option: string) => option}
          onChange={handleSelect}
          onInputChange={(_, val) => setInputValue(val)}
          autoSelect={false}
          renderInput={params => (
            <DevHubTextField
              {...params}
              label="Existing Permission Target"
              margin="normal"
              helperText="Search for an existing permission target"
              variant="outlined"
              required={required}
              InputProps={params.InputProps}
            />
          )}
        />
      )}
    </DevHubFormControl>
  );
};
